import React from "react";
import Blok from "../../../interfaces/Storyblok/Blok.interface";
import { StoryblokAsset } from "../../../interfaces/Storyblok/StoryblokAsset.interface";
import { StoryblokLink } from "../../../interfaces/Storyblok/StoryblokLink.interface";
import { YoutubeVideoBlok } from "../../../interfaces/MediaAsset/YoutubeVideoBlok.interface";
import Image from "../../atoms/Image/Image.atom";
import YoutubeVideo from "../../atoms/YoutubeVideo/YoutubeVideo.atom";
import CustomLink from "../../atoms/CustomLink/CustomLink.atom";
import isEmptyString from "../../../utils/isEmptyString/isEmptyString.util";

interface MediaHeroProps extends Blok {
  /** Base/desktop image. Rendered on-page at its natural aspect ratio. */
  image: StoryblokAsset;
  /**
   * Optional art-directed image for small screens. Supply a shorter/differently
   * cropped asset here so the hero doesn't appear too tall on mobile. Falls back
   * to `image` when not set.
   */
  mobile_image?: StoryblokAsset;
  /** Optional video. When a video_id is present it renders on-page (16:9) instead of the image. */
  video?: YoutubeVideoBlok[];
  /** Optional link — wraps the whole media so the hero is clickable. */
  link?: StoryblokLink;
  id?: string;
}

const assetExists = (asset?: StoryblokAsset): boolean => asset != null && !isEmptyString(asset.filename);

const MediaHero: React.FC<MediaHeroProps> = ({ image, mobile_image, video, link, id }) => {
  const hasVideo = Array.isArray(video) && video.length > 0 && !isEmptyString(video[0]?.video_id);
  const hasMobileImage = assetExists(mobile_image);

  const media = (
    <>
      {hasVideo ? (
        // On-page video keeps a 16:9 frame at every breakpoint.
        <div className="w-full aspect-video overflow-hidden">
          <YoutubeVideo video_id={video[0].video_id} autoplay loop />
        </div>
      ) : (
        <>
          {/* Mobile-specific crop (shorter/different aspect) when supplied. */}
          {hasMobileImage && (
            <div className="block sm:hidden">
              <Image image={mobile_image} objectFit="cover" fluidMaxWidth={768} />
            </div>
          )}
          {/* Base image. Hidden on small screens only when a mobile crop is supplied. */}
          {assetExists(image) && (
            <div className={hasMobileImage ? "hidden sm:block" : "block"}>
              <Image image={image} objectFit="cover" fluidMaxWidth={2560} />
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <section className="w-full overflow-hidden" id={id}>
      {link && !isEmptyString(link.cached_url) ? (
        <CustomLink url={link.cached_url} anchor={link.anchor}>
          {media}
        </CustomLink>
      ) : (
        media
      )}
    </section>
  );
};

export default MediaHero;
